import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { 
  TrendingUp, Calculator, Calendar, Users, 
  ChevronRight, Info, RefreshCw, AlertTriangle, Crown
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import SEO from '@/components/SEO';
import { getPartyConfig } from '@/lib/theme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Seat baseline for calculations
const SEAT_BASELINE = {
  labour: { seats: 411, votes: 33.7 },
  conservative: { seats: 121, votes: 23.7 },
  libdem: { seats: 72, votes: 12.2 },
  reform: { seats: 5, votes: 14.3 },
  green: { seats: 4, votes: 6.8 },
  snp: { seats: 9, votes: 2.5 },
  plaid: { seats: 4, votes: 0.7 },
  other: { seats: 24, votes: 6.1 }
};

// Stunning 3D Floating Pie Chart with rising segments
const FloatingPieChart3D = ({ data, activeIndex, onSegmentHover, onSegmentClick }) => {
  const containerRef = useRef(null);
  const total = data.reduce((sum, d) => sum + d.value, 0);
  
  // Calculate segment data
  let currentAngle = -90;
  const segments = data.map((item, index) => {
    const percentage = item.value / total;
    const angle = percentage * 360;
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    const midAngle = (startAngle + endAngle) / 2;
    currentAngle = endAngle;
    
    return {
      ...item,
      index,
      percentage,
      startAngle,
      endAngle,
      midAngle,
    };
  });

  const darkenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max((num >> 16) - amt, 0);
    const G = Math.max((num >> 8 & 0x00FF) - amt, 0);
    const B = Math.max((num & 0x0000FF) - amt, 0);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  };

  const lightenColor = (hex, percent) => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.min((num >> 16) + amt, 255);
    const G = Math.min((num >> 8 & 0x00FF) + amt, 255);
    const B = Math.min((num & 0x0000FF) + amt, 255);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
  };
  
  // Create SVG arc path
  const createArcPath = (startAngle, endAngle, outerRadius, innerRadius, centerX = 160, centerY = 160) => {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;
    
    const x1 = centerX + outerRadius * Math.cos(startRad);
    const y1 = centerY + outerRadius * Math.sin(startRad);
    const x2 = centerX + outerRadius * Math.cos(endRad);
    const y2 = centerY + outerRadius * Math.sin(endRad);
    
    const ix1 = centerX + innerRadius * Math.cos(startRad);
    const iy1 = centerY + innerRadius * Math.sin(startRad);
    const ix2 = centerX + innerRadius * Math.cos(endRad);
    const iy2 = centerY + innerRadius * Math.sin(endRad);
    
    return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1} Z`;
  };

  // Create 3D side path for segment
  const createSidePath = (startAngle, endAngle, outerRadius, height, centerX = 160, centerY = 160) => {
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const largeArc = (endAngle - startAngle) > 180 ? 1 : 0;
    
    const x1 = centerX + outerRadius * Math.cos(startRad);
    const y1 = centerY + outerRadius * Math.sin(startRad);
    const x2 = centerX + outerRadius * Math.cos(endRad);
    const y2 = centerY + outerRadius * Math.sin(endRad);
    
    return `M ${x1} ${y1} A ${outerRadius} ${outerRadius} 0 ${largeArc} 1 ${x2} ${y2} L ${x2} ${y2 + height} A ${outerRadius} ${outerRadius} 0 ${largeArc} 0 ${x1} ${y1 + height} Z`;
  };
  
  const outerRadius = 120;
  const innerRadius = 50;
  const baseHeight = 40;
  
  return (
    <div ref={containerRef} className="relative" style={{ perspective: '1000px', perspectiveOrigin: '50% 30%' }}>
      {/* Dynamic glow effect */}
      <div 
        className="absolute inset-0 blur-3xl rounded-full transition-all duration-500"
        style={{
          background: `radial-gradient(ellipse at center, ${data[activeIndex]?.color || '#3b82f6'}50 0%, transparent 60%)`,
          transform: 'translateY(80px) scale(1.2)',
          opacity: 0.4
        }}
      />
      
      {/* Main 3D Chart Container */}
      <div 
        className="relative mx-auto"
        style={{
          width: '320px',
          height: '320px',
          transform: 'rotateX(55deg) rotateZ(-12deg)',
          transformStyle: 'preserve-3d',
          animation: 'chartFloat 5s ease-in-out infinite',
        }}
      >
        <svg width="320" height="320" viewBox="0 0 320 320" style={{ overflow: 'visible' }}>
          <defs>
            {/* Gradients and filters for each segment */}
            {segments.map((seg, i) => (
              <linearGradient key={`topGrad-${i}`} id={`topGrad-${i}`} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor={lightenColor(seg.color, 25)} />
                <stop offset="50%" stopColor={seg.color} />
                <stop offset="100%" stopColor={darkenColor(seg.color, 15)} />
              </linearGradient>
            ))}
            {segments.map((seg, i) => (
              <linearGradient key={`sideGrad-${i}`} id={`sideGrad-${i}`} x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor={darkenColor(seg.color, 30)} />
                <stop offset="100%" stopColor={darkenColor(seg.color, 55)} />
              </linearGradient>
            ))}
            <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="4" result="coloredBlur"/>
              <feMerge>
                <feMergeNode in="coloredBlur"/>
                <feMergeNode in="SourceGraphic"/>
              </feMerge>
            </filter>
            <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="8" stdDeviation="6" floodOpacity="0.3"/>
            </filter>
          </defs>
          
          {/* Render segments (back to front based on angle for proper z-ordering) */}
          {segments
            .slice()
            .sort((a, b) => {
              // Segments at the back (top of screen in 3D view) render first
              const aMid = (a.startAngle + a.endAngle) / 2;
              const bMid = (b.startAngle + b.endAngle) / 2;
              // Active segment always on top
              if (a.index === activeIndex) return 1;
              if (b.index === activeIndex) return -1;
              return Math.sin(aMid * Math.PI / 180) - Math.sin(bMid * Math.PI / 180);
            })
            .map((seg) => {
              const isActive = seg.index === activeIndex;
              const midRad = (seg.midAngle * Math.PI) / 180;
              
              // Calculate rise and explode for active segment
              const riseY = isActive ? -25 : 0;
              const explodeX = isActive ? Math.cos(midRad) * 18 : 0;
              const explodeY = isActive ? Math.sin(midRad) * 18 : 0;
              const scale = isActive ? 1.06 : 1;
              const segHeight = isActive ? baseHeight + 15 : baseHeight;
              
              return (
                <g 
                  key={seg.index}
                  className="cursor-pointer"
                  style={{
                    transform: `translate(${explodeX}px, ${explodeY + riseY}px) scale(${scale})`,
                    transformOrigin: '160px 160px',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    filter: isActive ? 'url(#glow)' : 'url(#shadow)'
                  }}
                  onMouseEnter={() => onSegmentHover(seg.index)}
                  onMouseLeave={() => onSegmentHover(0)}
                  onClick={() => onSegmentClick(seg.index)}
                >
                  {/* 3D Side/Edge */}
                  <path
                    d={createSidePath(seg.startAngle, seg.endAngle, outerRadius, segHeight)}
                    fill={`url(#sideGrad-${seg.index})`}
                  />
                  
                  {/* Top surface */}
                  <path
                    d={createArcPath(seg.startAngle, seg.endAngle, outerRadius, innerRadius)}
                    fill={`url(#topGrad-${seg.index})`}
                    stroke="rgba(255,255,255,0.8)"
                    strokeWidth={isActive ? 3 : 1.5}
                  />
                  
                  {/* Highlight arc on top */}
                  {isActive && (
                    <path
                      d={createArcPath(seg.startAngle, seg.endAngle, outerRadius - 5, innerRadius + 10)}
                      fill="none"
                      stroke="rgba(255,255,255,0.4)"
                      strokeWidth="2"
                    />
                  )}
                </g>
              );
            })}
        </svg>
      </div>
      
      {/* Center floating info card */}
      <div 
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none z-10"
        style={{ marginTop: '-25px' }}
      >
        <div 
          className="text-center backdrop-blur-xl rounded-2xl px-6 py-4 shadow-2xl border transition-all duration-500"
          style={{ 
            background: 'linear-gradient(145deg, rgba(255,255,255,0.97) 0%, rgba(248,250,252,0.95) 100%)',
            borderColor: `${data[activeIndex]?.color}40`,
            boxShadow: `0 25px 50px -12px ${data[activeIndex]?.color}50, 0 0 0 1px rgba(255,255,255,0.1) inset`
          }}
        >
          <div 
            className="text-5xl font-black transition-all duration-300" 
            style={{ 
              color: data[activeIndex]?.color, 
              textShadow: `0 4px 12px ${data[activeIndex]?.color}30`,
              letterSpacing: '-0.02em'
            }}
          >
            {data[activeIndex]?.value.toFixed(1)}%
          </div>
          <div className="text-base font-bold text-slate-700 tracking-wide mt-1">
            {data[activeIndex]?.name}
          </div>
          {activeIndex === 0 && (
            <div className="text-xs font-bold text-emerald-600 mt-2 flex items-center justify-center gap-1.5 bg-emerald-50 rounded-full px-3 py-1">
              <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
              LEADING
            </div>
          )}
        </div>
      </div>
      
      {/* CSS Animations */}
      <style>{`
        @keyframes chartFloat {
          0%, 100% { 
            transform: rotateX(55deg) rotateZ(-12deg) translateY(0px); 
          }
          50% { 
            transform: rotateX(55deg) rotateZ(-12deg) translateY(-15px); 
          }
        }
      `}</style>
    </div>
  );
};

// Parliament semi-circle chart
const ParliamentChart = ({ seats }) => {
  if (!seats || Object.keys(seats).length === 0) return null;
  
  const sortedParties = Object.entries(seats)
    .filter(([_, s]) => s > 0)
    .sort((a, b) => b[1] - a[1]);
  
  const total = Object.values(seats).reduce((a, b) => a + b, 0);
  
  return (
    <div className="relative">
      <svg viewBox="0 0 200 110" className="w-full max-w-md mx-auto">
        {sortedParties.map(([party, seatCount], index) => {
          const startAngle = sortedParties.slice(0, index).reduce((acc, [_, s]) => acc + (s / total) * 180, 0);
          const angle = (seatCount / total) * 180;
          const config = getPartyConfig(party);
          
          const x1 = 100 + 90 * Math.cos((180 - startAngle) * Math.PI / 180);
          const y1 = 100 - 90 * Math.sin((180 - startAngle) * Math.PI / 180);
          const x2 = 100 + 90 * Math.cos((180 - startAngle - angle) * Math.PI / 180);
          const y2 = 100 - 90 * Math.sin((180 - startAngle - angle) * Math.PI / 180);
          
          const largeArc = angle > 90 ? 1 : 0;
          
          return (
            <path
              key={party}
              d={`M 100 100 L ${x1} ${y1} A 90 90 0 ${largeArc} 0 ${x2} ${y2} Z`}
              fill={config.color}
              stroke="white"
              strokeWidth="1"
            />
          );
        })}
        <text x="100" y="95" textAnchor="middle" className="text-xs font-bold fill-slate-600">seats</text>
      </svg>
      <div className="flex flex-wrap justify-center gap-3 mt-4">
        {sortedParties.slice(0, 6).map(([party, seatCount]) => {
          const config = getPartyConfig(party);
          return (
            <div key={party} className="flex items-center gap-1.5 text-sm">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: config.color }} />
              <span className="font-medium">{config.label}</span>
              <span className="text-slate-500">({seatCount})</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function PollsPage() {
  const [polls, setPolls] = useState([]);
  const [average, setAverage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const [activeTab, setActiveTab] = useState('tracker');
  const [activeIndex, setActiveIndex] = useState(0);

  // Seat calculator state
  const [voteShares, setVoteShares] = useState({
    labour: 19.0, conservative: 19.0, libdem: 12.0, reform: 30.0,
    green: 13.0, snp: 3.0, plaid: 1.0, other: 3.0
  });
  const [projectedSeats, setProjectedSeats] = useState(null);

  const presets = [
    { name: 'Current Polls', shares: { labour: 19, conservative: 19, libdem: 12, reform: 30, green: 13, snp: 3, plaid: 1, other: 3 } },
    { name: '2024 Election', shares: { labour: 34, conservative: 24, libdem: 12, reform: 14, green: 7, snp: 2.5, plaid: 0.7, other: 5.8 } },
    { name: 'Reform Surge', shares: { labour: 15, conservative: 15, libdem: 10, reform: 40, green: 10, snp: 3, plaid: 1, other: 6 } },
    { name: 'Labour Recovery', shares: { labour: 32, conservative: 20, libdem: 14, reform: 18, green: 8, snp: 3, plaid: 1, other: 4 } },
  ];

  useEffect(() => { fetchPollingData(); }, []);

  const fetchPollingData = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const timestamp = Date.now();
      const [pollsRes, avgRes] = await Promise.all([
        axios.get(`${API}/polls/latest?_t=${timestamp}`),
        axios.get(`${API}/polls/average?_t=${timestamp}`)
      ]);
      
      setPolls(pollsRes.data.polls || []);
      setAverage(avgRes.data.average || null);
      setLastFetched(new Date().toISOString());
      
      if (avgRes.data.average) {
        setVoteShares(prev => ({ ...prev, ...avgRes.data.average }));
      }
    } catch (error) {
      console.error('Failed to fetch polling data:', error);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleVoteShareChange = (party, value) => {
    setVoteShares(prev => ({ ...prev, [party]: value }));
  };

  useEffect(() => {
    if (activeTab === 'calculator') calculateSeatsLocally();
  }, [voteShares, activeTab]);

  const calculateSeatsLocally = () => {
    const seats = {};
    Object.entries(voteShares).forEach(([party, share]) => {
      const baseline = SEAT_BASELINE[party];
      if (baseline) {
        const swing = share - baseline.votes;
        const multiplier = party === 'reform' ? 8 : ['labour', 'conservative'].includes(party) ? 10 : party === 'snp' ? 15 : 4;
        seats[party] = Math.max(0, Math.round(baseline.seats + swing * multiplier));
      }
    });

    const total = Object.values(seats).reduce((a, b) => a + b, 0);
    if (total !== 650 && total > 0) {
      const scale = 650 / total;
      Object.keys(seats).forEach(party => { seats[party] = Math.round(seats[party] * scale); });
      const diff = 650 - Object.values(seats).reduce((a, b) => a + b, 0);
      const sorted = Object.entries(seats).sort((a, b) => b[1] - a[1]);
      if (sorted[0]) seats[sorted[0][0]] += diff;
    }

    const largest = Object.entries(seats).sort((a, b) => b[1] - a[1])[0];
    setProjectedSeats({
      seats, total_seats: 650,
      largest_party: largest?.[0] || 'labour',
      has_majority: largest?.[1] >= 326
    });
  };

  const applyPreset = (preset) => { setVoteShares(prev => ({ ...prev, ...preset.shares })); };

  // Create pie data
  const pieData = average ? Object.entries(average)
    .map(([party, value]) => ({
      name: getPartyConfig(party).label,
      value, color: getPartyConfig(party).color, party
    }))
    .filter(d => d.value >= 1)
    .sort((a, b) => b.value - a.value) : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (fetchError || !average) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center max-w-md">
          <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Unable to Load Polling Data</h2>
          <p className="text-slate-600 mb-4">Please check your connection and try again.</p>
          <Button onClick={fetchPollingData} className="bg-blue-600 hover:bg-blue-700">
            <RefreshCw className="h-4 w-4 mr-2" /> Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 pb-28 md:pb-20" data-testid="polls-page">
      <SEO 
        title="UK Political Polls & Opinion Tracker"
        description="Latest UK political polling data and voting intentions. Track Labour, Conservative, Reform UK and other party standings. Live poll aggregation and trends."
        keywords="UK polls, political polling UK, Labour polls, Conservative polls, Reform UK polls, voting intentions UK, opinion polls, UK election polls, poll tracker"
      />
      
      {/* Hero */}
      <section className="text-white py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/10 rounded-xl">
                <TrendingUp className="h-8 w-8" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-black">Poll Tracker</h1>
                <p className="text-slate-300">UK Voting Intention & Seat Calculator</p>
              </div>
            </div>
            <button onClick={fetchPollingData} className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg transition-all">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
          {lastFetched && (
            <div className="flex items-center gap-3 mt-2">
              <p className="text-xs text-slate-400">Updated: {new Date(lastFetched).toLocaleString()}</p>
            </div>
          )}
        </div>
      </section>

      {/* Subscribe Banner */}
      <div className="bg-gradient-to-r from-amber-500 to-amber-600 text-white py-3">
        <div className="max-w-7xl mx-auto px-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Crown className="h-5 w-5" />
            <span className="font-medium text-sm">Unlock unlimited access & premium features</span>
          </div>
          <Link to="/subscribe" className="bg-white text-amber-600 px-4 py-1.5 rounded-full text-sm font-bold hover:bg-amber-50 transition-all">
            Subscribe
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <section className="bg-slate-800/50 backdrop-blur border-b border-slate-700 sticky top-16 z-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-2 py-3">
            {['tracker', 'calculator'].map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all ${
                  activeTab === tab ? 'bg-white text-slate-900' : 'text-slate-300 hover:bg-slate-700'
                }`}
              >
                {tab === 'tracker' && <><TrendingUp className="h-4 w-4" /> Poll Tracker</>}
                {tab === 'calculator' && <><Calculator className="h-4 w-4" /> Seat Calculator</>}
              </button>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 py-8">
        
        {activeTab === 'tracker' && (
          <div className="space-y-8">
            {/* 3D Pie Chart Section */}
            <div className="bg-slate-800/50 backdrop-blur rounded-3xl border border-slate-700 p-8 overflow-hidden">
              <h2 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-cyan-400" />
                Current Polling Average
              </h2>
              
              <div className="grid lg:grid-cols-2 gap-8 items-center">
                {/* 3D Floating Pie Chart */}
                <div className="flex justify-center items-center min-h-[380px] py-8">
                  <FloatingPieChart3D 
                    data={pieData}
                    activeIndex={activeIndex}
                    onSegmentHover={setActiveIndex}
                    onSegmentClick={setActiveIndex}
                  />
                </div>
                
                {/* Party Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {pieData.slice(0, 8).map((party, idx) => (
                    <div 
                      key={party.party}
                      className={`p-4 rounded-xl cursor-pointer transition-all duration-300 ${
                        idx === activeIndex 
                          ? 'ring-2 ring-white shadow-lg scale-105 bg-white/20' 
                          : 'bg-slate-700/50 hover:bg-slate-700'
                      }`}
                      onClick={() => setActiveIndex(idx)}
                      onMouseEnter={() => setActiveIndex(idx)}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: party.color }} />
                        <span className="font-medium text-sm text-white">{party.name}</span>
                      </div>
                      <div className="text-2xl font-black" style={{ color: party.color }}>
                        {party.value.toFixed(1)}%
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Polls Table */}
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700 p-6">
              <h2 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-400" />
                Recent Polls
              </h2>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-600">
                      <th className="text-left py-3 px-2 font-semibold text-slate-300">Date</th>
                      <th className="text-left py-3 px-2 font-semibold text-slate-300">Pollster</th>
                      <th className="text-center py-3 px-2 font-semibold text-red-400">LAB</th>
                      <th className="text-center py-3 px-2 font-semibold text-blue-400">CON</th>
                      <th className="text-center py-3 px-2 font-semibold text-orange-400">LD</th>
                      <th className="text-center py-3 px-2 font-semibold text-cyan-400">REF</th>
                      <th className="text-center py-3 px-2 font-semibold text-green-400">GRN</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-700">
                    {polls.slice(0, 10).map((poll) => (
                      <tr key={poll.id} className="hover:bg-slate-700/50">
                        <td className="py-3 px-2 text-slate-400">{poll.date}</td>
                        <td className="py-3 px-2 font-medium text-white">{poll.pollster}</td>
                        <td className="py-3 px-2 text-center text-red-400 font-bold">{poll.results?.labour || '-'}</td>
                        <td className="py-3 px-2 text-center text-blue-400 font-bold">{poll.results?.conservative || '-'}</td>
                        <td className="py-3 px-2 text-center text-orange-400 font-bold">{poll.results?.libdem || '-'}</td>
                        <td className="py-3 px-2 text-center text-cyan-400 font-bold">{poll.results?.reform || '-'}</td>
                        <td className="py-3 px-2 text-center text-green-400 font-bold">{poll.results?.green || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'calculator' && (
          <div className="space-y-8">
            {/* Presets */}
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700 p-6">
              <h2 className="text-xl font-bold mb-4 text-white flex items-center gap-2">
                <Calculator className="h-5 w-5 text-purple-400" />
                What If Scenarios
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                {presets.map((preset, idx) => (
                  <button
                    key={idx}
                    onClick={() => applyPreset(preset)}
                    className="p-3 rounded-xl border-2 border-slate-600 hover:border-purple-400 hover:bg-purple-500/20 transition-all text-left"
                  >
                    <p className="font-semibold text-sm text-white">{preset.name}</p>
                    <p className="text-xs text-slate-400 mt-1">REF: {preset.shares.reform}% | LAB: {preset.shares.labour}%</p>
                  </button>
                ))}
              </div>
            </div>
            
            {/* Sliders */}
            <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700 p-6">
              <h2 className="text-lg font-bold mb-6 text-white">Adjust Vote Shares</h2>
              <div className="grid md:grid-cols-2 gap-6">
                {Object.entries(voteShares).filter(([p]) => p !== 'other').map(([party, value]) => {
                  const config = getPartyConfig(party);
                  return (
                    <div key={party} className="space-y-2">
                      <div className="flex justify-between items-center">
                        <label className="font-semibold" style={{ color: config.color }}>{config.label}</label>
                        <span className="text-lg font-bold text-white">{value.toFixed(1)}%</span>
                      </div>
                      <Slider value={[value]} onValueChange={([v]) => handleVoteShareChange(party, v)} min={0} max={50} step={0.5} />
                      <div className="flex justify-between text-xs text-slate-400">
                        <span>2024: {SEAT_BASELINE[party]?.votes || 0}%</span>
                        <span className={value > (SEAT_BASELINE[party]?.votes || 0) ? 'text-green-400' : 'text-red-400'}>
                          {value > (SEAT_BASELINE[party]?.votes || 0) ? '+' : ''}{(value - (SEAT_BASELINE[party]?.votes || 0)).toFixed(1)}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Projected Parliament */}
            {projectedSeats && (
              <div className="bg-slate-800/50 backdrop-blur rounded-2xl border border-slate-700 p-6">
                <h2 className="text-xl font-bold mb-6 text-white flex items-center gap-2">
                  <Users className="h-5 w-5 text-blue-400" />
                  Projected Parliament
                </h2>
                
                <div className={`mb-6 p-4 rounded-xl ${projectedSeats.has_majority ? 'bg-green-500/20 border border-green-500/50' : 'bg-amber-500/20 border border-amber-500/50'}`}>
                  <p className="font-bold text-lg text-white">
                    {projectedSeats.has_majority ? `${getPartyConfig(projectedSeats.largest_party).label} Majority` : 'Hung Parliament'}
                  </p>
                  <p className="text-sm text-slate-300">
                    {projectedSeats.has_majority 
                      ? `${projectedSeats.seats[projectedSeats.largest_party]} seats`
                      : `Largest: ${getPartyConfig(projectedSeats.largest_party).label} with ${projectedSeats.seats[projectedSeats.largest_party]} seats`}
                  </p>
                </div>
                
                <ParliamentChart seats={projectedSeats.seats} />
                
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-8">
                  {Object.entries(projectedSeats.seats).filter(([_, s]) => s > 0).sort((a, b) => b[1] - a[1]).map(([party, seats]) => {
                    const config = getPartyConfig(party);
                    const change = seats - (SEAT_BASELINE[party]?.seats || 0);
                    return (
                      <div key={party} className="p-4 rounded-xl bg-slate-700/50">
                        <div className="text-3xl font-black" style={{ color: config.color }}>{seats}</div>
                        <div className="text-sm font-medium text-slate-300">{config.label}</div>
                        <div className={`text-xs font-bold ${change >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {change >= 0 ? '+' : ''}{change} vs 2024
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
